import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, ShoppingBag, Edit, XCircle, Star, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { StatusBadge } from '../components/StatusBadge';
import { products } from '../data/mockData';
export function ProductDetails() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const product = products.find(p => p.id === id);
  const [isDisabled, setIsDisabled] = useState(false);
  if (!product) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-284">
        <p className="text-[#64748B]" data-id="element-285">Product not found</p>
      </div>;
  }
  return <div className="min-h-screen pb-10" data-id="element-286">
      <div className="max-w-md mx-auto" data-id="element-287">
        {/* Header */}
        <div className="flex items-center gap-4 p-5 pt-8" data-id="element-288">
          <button onClick={() => navigate('/products')} className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-white hover:bg-white/[0.08] transition-colors" data-id="element-289">
            <ArrowLeft className="w-5 h-5" data-id="element-290" />
          </button>
          <h1 className="text-white text-2xl font-bold tracking-tight" data-id="element-291">
            Product Details
          </h1>
        </div>

        <div className="px-5 space-y-4" data-id="element-292">
          {/* Product Image */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="glass-card overflow-hidden" data-id="element-293">
            <div className="h-52 relative overflow-hidden" data-id="element-294">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-295" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E1A] via-transparent to-transparent" data-id="element-296" />
              <div className="absolute top-3 right-3" data-id="element-297">
                <StatusBadge status={product.status} data-id="element-298" />
              </div>
              {product.isVerified && <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-[#00D68F] flex items-center justify-center shadow-[0_0_10px_rgba(0,214,143,0.5)]" data-id="element-299">
                  <CheckCircle className="w-5 h-5 text-[#0A0E1A]" data-id="element-300" />
                </div>}
            </div>

            <div className="p-6" data-id="element-301">
              <h2 className="text-white text-2xl font-bold tracking-tight mb-2" data-id="element-302">
                {product.name}
              </h2>

              <div className="flex items-center gap-3 mb-4" data-id="element-303">
                <span className="text-[#00D68F] text-3xl font-bold tracking-tight" data-id="element-304">
                  ₦{product.price.toLocaleString()}
                </span>
              </div>

              {/* Seller Info */}
              <div className="flex items-center gap-3 mb-5 pb-5 border-b border-white/[0.06]" data-id="element-305">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00D68F] to-[#00B377] flex items-center justify-center text-[#0A0E1A] text-xs font-bold" data-id="element-306">
                  {product.sellerName.split(' ').map(n => n[0]).join('')}
                </div>
                <span className="text-white font-medium" data-id="element-307">
                  {product.sellerName}
                </span>
                <div className="flex items-center gap-1 ml-auto" data-id="element-308">
                  <Star className="w-4 h-4 text-[#FFBE0B] fill-[#FFBE0B]" data-id="element-309" />
                  <span className="text-white font-bold" data-id="element-310">
                    {product.sellerRating}
                  </span>
                </div>
              </div>

              <p className="text-[#64748B] leading-relaxed mb-6" data-id="element-311">
                {product.description}
              </p>

              {product.status === 'approved' && <div className="flex gap-8 pt-5 border-t border-white/[0.06]" data-id="element-312">
                  <div className="flex items-center gap-3" data-id="element-313">
                    <div className="w-10 h-10 rounded-full bg-white/[0.04] flex items-center justify-center" data-id="element-314">
                      <Eye className="w-5 h-5 text-[#4CC9F0]" data-id="element-315" />
                    </div>
                    <div data-id="element-316">
                      <p className="text-white font-bold text-lg leading-none mb-1" data-id="element-317">
                        {product.views}
                      </p>
                      <p className="text-label" data-id="element-318">Views</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-319">
                    <div className="w-10 h-10 rounded-full bg-white/[0.04] flex items-center justify-center" data-id="element-320">
                      <ShoppingBag className="w-5 h-5 text-[#00D68F]" data-id="element-321" />
                    </div>
                    <div data-id="element-322">
                      <p className="text-white font-bold text-lg leading-none mb-1" data-id="element-323">
                        {product.sales}
                      </p>
                      <p className="text-label" data-id="element-324">Sales</p>
                    </div>
                  </div>
                </div>}
            </div>
          </motion.div>

          {/* Actions */}
          <div className="space-y-3" data-id="element-325">
            <motion.button whileTap={{
            scale: 0.97
          }} onClick={() => alert('Edit mode coming soon')} className="w-full glass-card border-[#00D68F]/30 text-[#00D68F] py-4 font-bold tracking-wide flex items-center justify-center gap-2 hover:bg-[#00D68F]/10 transition-colors" data-id="element-326">
              <Edit className="w-5 h-5" data-id="element-327" />
              Edit Product
            </motion.button>

            <motion.button whileTap={{
            scale: 0.97
          }} onClick={() => setIsDisabled(!isDisabled)} className={`w-full glass-card py-4 font-bold tracking-wide flex items-center justify-center gap-2 transition-colors ${isDisabled ? 'border-white/[0.1] text-[#64748B]' : 'border-[#FF4757]/30 text-[#FF4757] hover:bg-[#FF4757]/10'}`} data-id="element-328">
              <XCircle className="w-5 h-5" data-id="element-329" />
              {isDisabled ? 'Product Disabled' : 'Disable Product'}
            </motion.button>
          </div>

          {product.status === 'pending' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-[#FFBE0B]/10 border border-[#FFBE0B]/20 rounded-2xl p-5 text-center" data-id="element-330">
              <p className="text-[#FFBE0B] font-bold tracking-tight" data-id="element-331">
                ⏳ Waiting for Approval
              </p>
              <p className="text-[#FFBE0B]/70 text-sm mt-1" data-id="element-332">
                Your product is under review
              </p>
            </motion.div>}

          {product.status === 'rejected' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-[#FF4757]/10 border border-[#FF4757]/20 rounded-2xl p-5 text-center" data-id="element-333">
              <p className="text-[#FF4757] font-bold tracking-tight" data-id="element-334">
                ❌ Product Rejected
              </p>
              <p className="text-[#FF4757]/70 text-sm mt-1" data-id="element-335">
                Please contact support for details
              </p>
            </motion.div>}
        </div>
      </div>
    </div>;
}